import type maplibregl from "maplibre-gl";
import type { StyleValue } from "../types";
import { buildCaseExpression } from "./buildCaseExpression";
import { buildGlobalStateMultiplier } from "./buildGlobalStateMultiplier";
import { buildMatchExpression } from "./buildMatchExpression";

type ColorMatch = {
	cases: Array<[label: string, value: string]>;
	fallback: string;
};

type NumberMatch = {
	cases: Array<[label: string, value: number]>;
	fallback: number;
};

type Props =
	| { literal: { idle: string; matched?: string; hover: string } }
	| { globalStateModifierKey?: string; literal: { idle: number; matched?: number; hover: number } }
	| { expression: { attribute: string; idle: ColorMatch; hover: ColorMatch } }
	| { globalStateModifierKey?: string; expression: { attribute: string; idle: NumberMatch; hover: NumberMatch } };

function buildStatefulExpression(props: Props): maplibregl.ExpressionSpecification {
	const isHovered = ["boolean", ["feature-state", "hover"], false] as unknown as maplibregl.ExpressionSpecification;
	const matchesFilter = ["boolean", ["feature-state", "matchesFilter"], false] as unknown as maplibregl.ExpressionSpecification;

	let idleValue: string | number | maplibregl.ExpressionSpecification;
	let matchedValue: string | number | undefined;
	let hoverValue: string | number | maplibregl.ExpressionSpecification;

	if ("literal" in props) {
		idleValue = props.literal.idle;
		matchedValue = props.literal.matched;
		hoverValue = props.literal.hover;
	} else {
		const { attribute, idle, hover } = props.expression;
		idleValue = buildMatchExpression({ attribute, cases: idle.cases, fallback: idle.fallback });
		hoverValue = buildMatchExpression({ attribute, cases: hover.cases, fallback: hover.fallback });
	}

	const cases: Array<[maplibregl.ExpressionSpecification, StyleValue]> = [[isHovered, hoverValue]];

	if (matchedValue !== undefined) {
		cases.push([matchesFilter, matchedValue]);
	}

	const switched = buildCaseExpression({ cases, fallback: idleValue });

	const globalStateModifierKey = "globalStateModifierKey" in props ? props.globalStateModifierKey : undefined;

	if (globalStateModifierKey === undefined) {
		return switched;
	}

	return buildGlobalStateMultiplier({ stateKey: globalStateModifierKey, value: switched });
}

export { buildStatefulExpression as buildStateExpression };
